import { Delete } from '@mui/icons-material';
import { List, ListItem, MenuItem } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { PresentationComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import VyMultiSelect from '../../../../common/VyMultiSelect';
import VySelect from '../../../../common/VySelect';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';

interface PresentationSettingsTableInterface {
  component: PresentationComponentInterface;
  onChange: (c: PresentationComponentInterface) => void;
}

const PresentationSettingsTable = ({ component, onChange }: PresentationSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);
  const slots = useSelector(({ common }) => common.slots);

  const handleSpotlightsChange = (values: string[]) => {
    const spotlightPresentations = slots.filter((b) => values.includes(b.meetingroom._id)).map((b) => b.meetingroom._id);
    onChange({ ...component, spotlightPresentations });
  };

  const formattedMSOptions = slots.map((b) => ({ value: b.meetingroom._id, name: b.meetingroom.name || '' }));
  const formattedMSSelectedOptions =
    component.spotlightPresentations?.map((sb) => ({ value: sb, name: slots.find((b) => b.meetingroom._id === sb)?.meetingroom.name || '' })) || [];

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('presentation-component-style'),
      settings: [
        {
          label: t('presentation-template'),
          content: (
            <VySelect
              onChange={(e) => {
                const _component = {
                  ...component,
                  template: e.target.value as string,
                };
                onChange(_component);
              }}
              value={component.template || 'normal'}
              fullWidth>
              <MenuItem value="normal">{t('presentation-normal')}</MenuItem>
              <MenuItem value="timeline">{t('presentation-timeline')}</MenuItem>
              <MenuItem value="list">{t('presentation-list')}</MenuItem>
            </VySelect>
          ),
        },
        {
          label: t('pagebuilder:presentation-showPresentationStatus'),
          type: 'boolean',
          path: 'showPresentationStatus',
        },
        {
          label: t('pagebuilder:presentation-showdate'),
          type: 'boolean',
          path: 'showDate',
        },
        {
          label: t('pagebuilder:presentation-showtime'),
          type: 'boolean',
          path: 'showTime',
        },
        {
          label: t('pagebuilder:slot-show-start-end-time'),
          tooltip: t('pagebuilder:slot-show-start-end-time-tooltip'),
          type: 'boolean',
          path: 'showStartEndTime',
        },
      ],
    },
    {
      name: t('presentation-component-grouping'),
      settings: [
        {
          label: t('pagebuilder:presentation-group-by-start-time'),
          type: 'boolean',
          path: 'groupByStartTime',
          tooltip: t('pagebuilder:presentation-group-by-start-time-tooltip'),
        },
      ],
    },
    {
      name: t('presentation-component-visibility'),
      settings: [
        {
          label: t('pagebuilder:presentation-hide-past'),
          type: 'boolean',
          path: 'hidePastPresentations',
          tooltip: t('pagebuilder:presentation-hide-past-tooltip'),
        },
      ],
    },
    {
      name: t('presentation-highlight'),
      settings: [
        {
          label: t('pagebuilder:presentations-spotlight'),
          content: (
            <VyMultiSelect
              name={`presentation-spotlight-${component._id}`}
              onValueChange={handleSpotlightsChange}
              options={formattedMSOptions}
              selectedOptions={formattedMSSelectedOptions}
            />
          ),
        },
      ],
    },
    {
      name: t('presentation-select'),
      settings: [
        {
          label: t('pagebuilder:available-presentations'),
          content: (
            <List component="div" disablePadding className="break-words hyphens-auto">
              {slots
                .filter((slot) => {
                  return !component?.selectedPresentations?.find((p) => p === slot._id);
                })
                .map((slot) => (
                  <>
                    <div className="flex flex-row mb-1">
                      <ListItem
                        button
                        selected={component?.selectedPresentations?.find((p) => p === slot._id) ? true : false}
                        onClick={() => {
                          const seletedPresentations = component.selectedPresentations?.slice() ?? [];
                          seletedPresentations?.push(slot._id);
                          onChange({ ...component, selectedPresentations: seletedPresentations });
                        }}>
                        <span className="flex-1">
                          {slot?.meetingroom?.name} <i>{slot?.datetime_start && `[${format(new Date(slot?.datetime_start), 'dd.MM. HH:mm')}]`}</i>
                        </span>
                        {component?.selectedPresentations?.find((p) => p === slot._id) && <span>✓</span>}
                      </ListItem>
                    </div>
                  </>
                ))}
            </List>
          ),
        },
        {
          label: t('pagebuilder:selected-presentations'),
          content: (
            <List component="div" disablePadding>
              {component.selectedPresentations
                ?.slice()
                .map((selectePresentation) => {
                  return slots.find((slot) => selectePresentation === slot._id);
                })
                .map((slot, i) => (
                  <>
                    <div className="flex flex-row mb-1">
                      <ListItem>
                        <span className="flex-1">
                          {slot?.meetingroom?.name} <i>{slot?.datetime_start && `[${format(new Date(slot?.datetime_start), 'dd.MM. HH:mm')}]`}</i>
                        </span>
                      </ListItem>
                      <ListItem
                        style={{ flex: 0 }}
                        button
                        onClick={() => {
                          const selectedPresentations = component.selectedPresentations?.slice() ?? [];
                          const slotIndex = selectedPresentations?.findIndex((p) => p === slot?._id);
                          if (slotIndex !== undefined) {
                            selectedPresentations?.splice(slotIndex, 1);
                            onChange({ ...component, selectedPresentations });
                          }
                        }}>
                        <Delete />
                      </ListItem>
                    </div>
                  </>
                ))}
            </List>
          ),
        },
      ],
    },
  ];

  return <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />;
};
export default PresentationSettingsTable;
